import { formatDateTimeInTz, formatPriceMinor } from '@/lib/format';
/**
 * Sub-spec 08 Plan 2 §2.1 — table for report rows, driven by per-column
 * `kind` from the API response.
 */
import { cn } from '@/lib/utils';

export type ReportColumnKind =
  | 'string'
  | 'number'
  | 'money_minor'
  | 'percent'
  | 'date'
  | 'datetime'
  | 'boolean';

export interface ReportColumn {
  key: string;
  label: string;
  kind: ReportColumnKind;
  align?: 'left' | 'right';
}

export type ReportRow = Record<string, unknown>;

interface Props {
  columns: ReadonlyArray<ReportColumn>;
  rows: ReadonlyArray<ReportRow>;
  currency: string;
  timezone: string;
}

function rightAlign(kind: ReportColumnKind, hint?: 'left' | 'right'): boolean {
  if (hint === 'right') return true;
  if (hint === 'left') return false;
  return kind === 'number' || kind === 'money_minor' || kind === 'percent';
}

function renderCell(value: unknown, kind: ReportColumnKind, currency: string, tz: string): string {
  if (value === null || value === undefined) return '';
  switch (kind) {
    case 'money_minor': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return '';
      return formatPriceMinor(n, currency);
    }
    case 'percent': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return '';
      return `${n.toFixed(2)}%`;
    }
    case 'date':
      return String(value);
    case 'datetime': {
      try {
        return formatDateTimeInTz(String(value), tz);
      } catch {
        return String(value);
      }
    }
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'number':
      return typeof value === 'number' ? String(value) : String(Number(value) || 0);
    default:
      return String(value);
  }
}

export function ReportTable({ columns, rows, currency, timezone }: Props) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground">
        No data for this date range.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-secondary/50 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'border-b border-border px-3 py-2 font-medium',
                  rightAlign(col.kind, col.align) ? 'text-right' : 'text-left',
                )}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-border last:border-0">
              {columns.map((col) => {
                const isNumeric =
                  col.kind === 'number' || col.kind === 'money_minor' || col.kind === 'percent';
                return (
                  <td
                    key={col.key}
                    className={cn(
                      'px-3 py-2',
                      rightAlign(col.kind, col.align) ? 'text-right' : 'text-left',
                      isNumeric && 'num',
                    )}
                  >
                    {renderCell(row[col.key], col.kind, currency, timezone)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
